import { c as _c } from "react/compiler-runtime";
/* eslint-disable no-nested-ternary */
/* eslint-disable react/no-array-index-key */
import { Button, cn, Dialog, DialogContent, DialogHeader, DialogTitle, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@newfront-insurance/core-ui/v2';
import { useAnalytics } from '@newfront-insurance/data-layer-client';
import { dataToCSV, dataToTSV, downloadAsCSV, copyToClipboard, type TableData } from '@newfront-insurance/nova-core';
import { Check, Copy, Download, Maximize2 } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
interface TableWrapperProps {
  children: React.ReactNode;
  className?: string;
  messageUuid?: string;
  prefix?: string;
  onExpandTable?: (tableData: TableData) => void;
}
export function TableWrapper(t0) {
  const $ = _c(109);
  let children;
  let className;
  let messageUuid;
  let onExpandTable;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      children,
      className,
      messageUuid,
      onExpandTable,
      prefix: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = messageUuid;
    $[4] = onExpandTable;
    $[5] = props;
    $[6] = t1;
  } else {
    children = $[1];
    className = $[2];
    messageUuid = $[3];
    onExpandTable = $[4];
    props = $[5];
    t1 = $[6];
  }
  const prefix = t1 === undefined ? "nova" : t1;
  let t2;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      headers: [],
      rows: []
    };
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const [tableData, setTableData] = useState(t2);
  const [copying, setCopying] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const analytics = useAnalytics();
  const tableRef = useRef(null);
  const copyTimeoutRef = useRef(null);
  let t3;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      if (tableRef.current) {
        const extractTableData = () => {
          const table = tableRef.current;
          if (!table) {
            return;
          }
          const headers = [];
          const rows = [];
          const headerCells = table.querySelectorAll("thead th");
          headerCells.forEach(cell => {
            headers.push(cell.textContent?.trim() || "");
          });
          const bodyRows = table.querySelectorAll("tbody tr");
          bodyRows.forEach(row => {
            const rowData = [];
            const cells = row.querySelectorAll("td");
            cells.forEach(cell_0 => {
              rowData.push(cell_0.textContent?.trim() || "");
            });
            if (rowData.length > 0) {
              rows.push(rowData);
            }
          });
          setTableData({
            headers,
            rows
          });
        };
        const timer = setTimeout(extractTableData, 100);
        return () => clearTimeout(timer);
      }
    };
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== children) {
    t4 = [children];
    $[9] = children;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  useEffect(t3, t4);
  let t5;
  if ($[11] !== analytics || $[12] !== messageUuid || $[13] !== tableData.headers || $[14] !== tableData.rows) {
    t5 = async inFullscreen => {
      if (tableData.headers.length === 0) {
        return;
      }
      analytics.track({
        event: "nova-table-copied",
        properties: {
          inFullscreen,
          messageUuid
        }
      });
      setCopying(true);
      const tsvContent = dataToTSV(tableData.headers, tableData.rows);
      await copyToClipboard(tsvContent);
      setCopying(false);
      setCopied(true);
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
      copyTimeoutRef.current = window.setTimeout(() => {
        setCopied(false);
        copyTimeoutRef.current = null;
      }, 2000);
    };
    $[11] = analytics;
    $[12] = messageUuid;
    $[13] = tableData.headers;
    $[14] = tableData.rows;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const handleCopy = t5;
  let t6;
  let t7;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
    t7 = [];
    $[16] = t6;
    $[17] = t7;
  } else {
    t6 = $[16];
    t7 = $[17];
  }
  useEffect(t6, t7);
  let t8;
  if ($[18] !== analytics || $[19] !== messageUuid || $[20] !== prefix || $[21] !== tableData.headers || $[22] !== tableData.rows) {
    t8 = inFullscreen_0 => {
      analytics.track({
        event: `${prefix}-table-downloaded`,
        properties: {
          inFullscreen: inFullscreen_0,
          messageUuid
        }
      });
      if (tableData.headers.length === 0) {
        return;
      }
      const csvContent = dataToCSV(tableData.headers, tableData.rows);
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
      downloadAsCSV(`table-export-${timestamp}.csv`, csvContent);
    };
    $[18] = analytics;
    $[19] = messageUuid;
    $[20] = prefix;
    $[21] = tableData.headers;
    $[22] = tableData.rows;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const handleDownload = t8;
  const hasExpandCallback = !!onExpandTable;
  const isDisabled = tableData.headers.length === 0;
  let t9;
  if ($[24] !== className) {
    t9 = cn("divide-steel-200 border-steel-200 bg-steel-50 w-max min-w-full divide-y border-0", className);
    $[24] = className;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== children || $[27] !== props || $[28] !== t9) {
    t10 = <div className="border-steel-200 w-full max-w-full overflow-x-auto overscroll-x-contain rounded-xs border"><table ref={tableRef} className={t9} {...props}>{children}</table></div>;
    $[26] = children;
    $[27] = props;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== analytics || $[31] !== hasExpandCallback || $[32] !== messageUuid || $[33] !== onExpandTable || $[34] !== prefix || $[35] !== tableData) {
    t11 = () => {
      analytics.track({
        event: `${prefix}-table-fullscreen`,
        properties: {
          messageUuid
        }
      });
      if (hasExpandCallback) {
        onExpandTable(tableData);
      }
      setIsDialogOpen(!hasExpandCallback);
    };
    $[30] = analytics;
    $[31] = hasExpandCallback;
    $[32] = messageUuid;
    $[33] = onExpandTable;
    $[34] = prefix;
    $[35] = tableData;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  let t12;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Maximize2 className="h-4 w-4" />;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  let t13;
  if ($[38] !== isDisabled || $[39] !== t11) {
    t13 = <TooltipTrigger asChild={true}><Button variant="transparent" size="small" disabled={isDisabled} onClick={t11}>{t12}</Button></TooltipTrigger>;
    $[38] = isDisabled;
    $[39] = t11;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <TooltipContent><p>View table in fullscreen</p></TooltipContent>;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== t13) {
    t15 = <Tooltip>{t13}{t14}</Tooltip>;
    $[42] = t13;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      zIndex: 9999
    };
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <DialogHeader><DialogTitle>Table View</DialogTitle></DialogHeader>;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== tableData.headers) {
    t18 = tableData.headers.map(_temp);
    $[46] = tableData.headers;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  let t19;
  if ($[48] !== t18) {
    t19 = <thead className="bg-steel-100 rounded-xs"><tr>{t18}</tr></thead>;
    $[48] = t18;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  let t20;
  if ($[50] !== tableData.rows) {
    t20 = tableData.rows.map(_temp2);
    $[50] = tableData.rows;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  let t21;
  if ($[52] !== t20) {
    t21 = <tbody className="divide-steel-200 divide-y bg-white">{t20}</tbody>;
    $[52] = t20;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  let t22;
  if ($[54] !== t19 || $[55] !== t21) {
    t22 = <div className="overflow-auto"><table className="divide-steel-200 border-steel-200 bg-steel-50 min-w-full divide-y rounded-xs border">{t19}{t21}</table></div>;
    $[54] = t19;
    $[55] = t21;
    $[56] = t22;
  } else {
    t22 = $[56];
  }
  let t23;
  if ($[57] !== handleCopy) {
    t23 = () => handleCopy(true);
    $[57] = handleCopy;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  const t24 = copying || isDisabled;
  let t25;
  if ($[59] !== copied) {
    t25 = copied ? <Check className="mr-1 h-4 w-4 text-green-600" /> : <Copy className="mr-1 h-4 w-4" />;
    $[59] = copied;
    $[60] = t25;
  } else {
    t25 = $[60];
  }
  const t26 = copying ? "Copying..." : copied ? "Copied!" : "Copy";
  let t27;
  if ($[61] !== t23 || $[62] !== t24 || $[63] !== t25 || $[64] !== t26) {
    t27 = <Button variant="secondary" size="small" onClick={t23} disabled={t24}>{t25}{t26}</Button>;
    $[61] = t23;
    $[62] = t24;
    $[63] = t25;
    $[64] = t26;
    $[65] = t27;
  } else {
    t27 = $[65];
  }
  let t28;
  if ($[66] !== handleDownload) {
    t28 = () => handleDownload(true);
    $[66] = handleDownload;
    $[67] = t28;
  } else {
    t28 = $[67];
  }
  let t29;
  if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <Download className="mr-1 h-4 w-4" />;
    $[68] = t29;
  } else {
    t29 = $[68];
  }
  let t30;
  if ($[69] !== isDisabled || $[70] !== t28) {
    t30 = <Button variant="secondary" size="small" onClick={t28} disabled={isDisabled}>{t29}Download CSV</Button>;
    $[69] = isDisabled;
    $[70] = t28;
    $[71] = t30;
  } else {
    t30 = $[71];
  }
  let t31;
  if ($[72] !== t27 || $[73] !== t30) {
    t31 = <div className="mt-4 flex justify-start gap-2">{t27}{t30}</div>;
    $[72] = t27;
    $[73] = t30;
    $[74] = t31;
  } else {
    t31 = $[74];
  }
  let t32;
  if ($[75] !== t22 || $[76] !== t31) {
    t32 = <DialogContent className="max-h-[90vh] max-w-7xl overflow-auto [&>*]:!z-[9999]" style={t16}>{t17}{t22}{t31}</DialogContent>;
    $[75] = t22;
    $[76] = t31;
    $[77] = t32;
  } else {
    t32 = $[77];
  }
  let t33;
  if ($[78] !== isDialogOpen || $[79] !== t32) {
    t33 = <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>{t32}</Dialog>;
    $[78] = isDialogOpen;
    $[79] = t32;
    $[80] = t33;
  } else {
    t33 = $[80];
  }
  let t34;
  if ($[81] !== handleCopy) {
    t34 = () => handleCopy(false);
    $[81] = handleCopy;
    $[82] = t34;
  } else {
    t34 = $[82];
  }
  const t35 = copying || isDisabled;
  let t36;
  if ($[83] !== copied) {
    t36 = copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />;
    $[83] = copied;
    $[84] = t36;
  } else {
    t36 = $[84];
  }
  let t37;
  if ($[85] !== t34 || $[86] !== t35 || $[87] !== t36) {
    t37 = <TooltipTrigger asChild={true}><Button variant="transparent" size="small" onClick={t34} disabled={t35}>{t36}</Button></TooltipTrigger>;
    $[85] = t34;
    $[86] = t35;
    $[87] = t36;
    $[88] = t37;
  } else {
    t37 = $[88];
  }
  let t38;
  if ($[89] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <TooltipContent><p>Copy table to clipboard</p></TooltipContent>;
    $[89] = t38;
  } else {
    t38 = $[89];
  }
  let t39;
  if ($[90] !== t37) {
    t39 = <Tooltip>{t37}{t38}</Tooltip>;
    $[90] = t37;
    $[91] = t39;
  } else {
    t39 = $[91];
  }
  let t40;
  if ($[92] !== handleDownload) {
    t40 = () => handleDownload(false);
    $[92] = handleDownload;
    $[93] = t40;
  } else {
    t40 = $[93];
  }
  let t41;
  if ($[94] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <Download className="h-4 w-4" />;
    $[94] = t41;
  } else {
    t41 = $[94];
  }
  let t42;
  if ($[95] !== isDisabled || $[96] !== t40) {
    t42 = <TooltipTrigger asChild={true}><Button variant="transparent" size="small" onClick={t40} disabled={isDisabled}>{t41}</Button></TooltipTrigger>;
    $[95] = isDisabled;
    $[96] = t40;
    $[97] = t42;
  } else {
    t42 = $[97];
  }
  let t43;
  if ($[98] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = <TooltipContent><p>Download table as CSV</p></TooltipContent>;
    $[98] = t43;
  } else {
    t43 = $[98];
  }
  let t44;
  if ($[99] !== t42) {
    t44 = <Tooltip>{t42}{t43}</Tooltip>;
    $[99] = t42;
    $[100] = t44;
  } else {
    t44 = $[100];
  }
  let t45;
  if ($[101] !== t15 || $[102] !== t33 || $[103] !== t39 || $[104] !== t44) {
    t45 = <TooltipProvider><div className="border-steel-200 bg-steel-50 flex items-center justify-start gap-2 overflow-x-auto rounded-b-xs border-x border-b p-2">{t15}{t33}{t39}{t44}</div></TooltipProvider>;
    $[101] = t15;
    $[102] = t33;
    $[103] = t39;
    $[104] = t44;
    $[105] = t45;
  } else {
    t45 = $[105];
  }
  let t46;
  if ($[106] !== t10 || $[107] !== t45) {
    t46 = <div className="my-4 w-full">{t10}{t45}</div>;
    $[106] = t10;
    $[107] = t45;
    $[108] = t46;
  } else {
    t46 = $[108];
  }
  return t46;
}
function _temp2(row_0, rowIndex) {
  return <tr key={`row-${rowIndex}-${row_0.join("-")}`} className="hover:bg-steel-50">{row_0.map((cell_1, cellIndex) => <td key={`cell-${rowIndex}-${cellIndex}-${cell_1}`} className="border-steel-200 text-steel-500 border-r px-4 py-3 text-sm whitespace-nowrap last:border-r-0">{cell_1}</td>)}</tr>;
}
function _temp(header, headerIndex) {
  return <th key={`header-${headerIndex}-${header}`} className="border-steel-200 text-steel-500 border-r px-4 py-3 text-left text-xs font-medium tracking-wider whitespace-nowrap uppercase last:border-r-0">{header}</th>;
}